import { use, useMemo } from 'react'
import { CompanyBillingContext } from '@/components/company-billing/context'
import { QueryRefsContext } from '@/components/root/query-refs-context'
import { useReadQuery } from '@/lib/hooks'
import { isExpiredCard } from '@/lib/utils'

export const CompanyBillingProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { companyBillingQueryRef } = use(QueryRefsContext)
  const { data } = useReadQuery(companyBillingQueryRef)

  const remainingCredits = useMemo(() => {
    const stripeBalance =
      data?.company?.sevallaBilling?.creditUsage?.remainingCredits ?? 0
    return Math.floor(stripeBalance / 100)
  }, [data])

  const hasValidBankcard = useMemo(() => {
    const method = data?.company?.defaultPaymentMethod

    if (!method?.id) {
      return false
    }

    return !isExpiredCard({
      expYear: method.expYear,
      expMonth: method.expMonth,
    })
  }, [data])

  const hasCompany = Boolean(data?.company?.id)

  const hasPaymentMethod = Boolean(data?.company?.defaultPaymentMethod?.id)

  const hasBillingDetails = Boolean(
    data?.company?.sevallaBilling?.hasValidBillingAddress
  )

  return (
    <CompanyBillingContext.Provider
      value={{
        remainingCredits,
        hasCompany,
        hasValidBankcard,
        hasPaymentMethod,
        hasBillingDetails,
        isInsolvent: remainingCredits <= 0 && !hasValidBankcard,
      }}
    >
      {children}
    </CompanyBillingContext.Provider>
  )
}
